import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { photoUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { BadgeCheck, Star, Users, Crown, Plus, Trash2, ImagePlus, X } from "lucide-react";

const STATUS_LABEL = { pending: "Em análise", approved: "Aprovado", suspended: "Suspenso" };
const PLAN_LABEL = { trial: "Trial (7 dias grátis)", basico: "Básico", pro: "Pro", premium: "Premium" };

function RegisterForm({ meta, onDone }) {
  const [form, setForm] = useState({
    business_name: "", category: "", description: "", phone: "", whatsapp: "",
    neighborhoods: [], address: "", hours: "", document: "", services: [],
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.business_name || !form.category || !form.whatsapp) return toast.error("Preencha nome, categoria e WhatsApp");
    setSaving(true);
    try {
      await api.post("/providers/register", { ...form, whatsapp: form.whatsapp.replace(/\D/g, "") });
      toast.success("Perfil enviado para análise! Você já pode aproveitar o trial de 7 dias.");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao cadastrar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto bg-white rounded-3xl p-8 border" data-testid="provider-register-form">
      <h2 className="font-heading font-bold text-2xl text-brand-graphite mb-1">Cadastre seu negócio</h2>
      <p className="text-sm text-muted-foreground mb-6">Após o envio, nossa equipe verifica seus dados e aprova seu perfil (trial de 7 dias incluso).</p>
      <div className="space-y-4">
        <div><Label>Nome do negócio *</Label><Input value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} className="mt-1" data-testid="prov-name-input" /></div>
        <div>
          <Label>Categoria *</Label>
          <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
            <SelectTrigger className="mt-1" data-testid="prov-category-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
            <SelectContent>{meta.categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Descrição</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" data-testid="prov-description-input" /></div>
        <div className="grid grid-cols-2 gap-4">
          <div><Label>Telefone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(47) 3333-0000" className="mt-1" data-testid="prov-phone-input" /></div>
          <div><Label>WhatsApp *</Label><Input value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} placeholder="5547999999999" className="mt-1" data-testid="prov-whatsapp-input" /></div>
        </div>
        <div>
          <Label>Bairros de atendimento</Label>
          <div className="flex flex-wrap gap-2 mt-2" data-testid="prov-neighborhoods">
            {meta.neighborhoods.map((n) => (
              <button key={n} type="button"
                onClick={() => setForm({ ...form, neighborhoods: form.neighborhoods.includes(n) ? form.neighborhoods.filter((x) => x !== n) : [...form.neighborhoods, n] })}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${form.neighborhoods.includes(n) ? "bg-brand-mint text-white border-brand-mint" : "hover:border-brand-mint"}`}>
                {n}
              </button>
            ))}
          </div>
        </div>
        <div><Label>Endereço</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1" data-testid="prov-address-input" /></div>
        <div className="grid grid-cols-2 gap-4">
          <div><Label>Horários</Label><Input value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} placeholder="Seg–Sáb, 8h às 18h" className="mt-1" data-testid="prov-hours-input" /></div>
          <div><Label>CPF/CNPJ (verificação)</Label><Input value={form.document} onChange={(e) => setForm({ ...form, document: e.target.value })} className="mt-1" data-testid="prov-document-input" /></div>
        </div>
        <Button onClick={submit} disabled={saving} className="w-full bg-brand-coral hover:bg-brand-coralDark text-white rounded-full h-11" data-testid="prov-register-submit">
          {saving ? "Enviando..." : "Enviar para análise"}
        </Button>
      </div>
    </div>
  );
}

export default function ProviderDashboard() {
  const [provider, setProvider] = useState(null);
  const [stats, setStats] = useState(null);
  const [meta, setMeta] = useState({ categories: [], neighborhoods: [] });
  const [notFound, setNotFound] = useState(false);
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const uploadPhoto = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/upload", fd);
        setEdit((prev) => ({ ...prev, photos: [...(prev.photos || []), r.data.path] }));
      }
      toast.success("Foto(s) enviada(s)! Não esqueça de salvar.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao enviar foto");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const load = () => {
    api.get("/provider/me").then((r) => { setProvider(r.data); setEdit({ description: r.data.description, phone: r.data.phone, whatsapp: r.data.whatsapp, address: r.data.address, hours: r.data.hours, services: r.data.services || [], photos: r.data.photos || [] }); setNotFound(false); })
      .catch((e) => { if (e.response?.status === 404) setNotFound(true); });
    api.get("/provider/me/stats").then((r) => setStats(r.data)).catch(() => {});
  };

  useEffect(() => {
    api.get("/meta").then((r) => setMeta(r.data));
    load();
  }, []);

  if (notFound) return <div className="max-w-7xl mx-auto px-6 py-12"><RegisterForm meta={meta} onDone={load} /></div>;
  if (!provider) return <div className="max-w-7xl mx-auto px-6 py-20 text-center text-muted-foreground">Carregando...</div>;

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/provider/me", edit);
      toast.success("Perfil atualizado!");
      load();
    } catch {
      toast.error("Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-10" data-testid="provider-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold font-heading text-brand-graphite">{provider.business_name}</h1>
            {provider.verified && <BadgeCheck className="w-6 h-6 text-brand-mint" data-testid="dashboard-verified-badge" />}
          </div>
          <p className="text-muted-foreground mt-1">{meta.categories.find((c) => c.id === provider.category)?.name}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs font-semibold px-3 py-1.5 rounded-full ${provider.status === "approved" ? "bg-brand-mintLight text-brand-mintDark" : provider.status === "pending" ? "bg-brand-mustardLight text-brand-mustardDark" : "bg-red-100 text-red-700"}`} data-testid="provider-status-badge">
            {STATUS_LABEL[provider.status]}
          </span>
          <span className="text-xs font-semibold px-3 py-1.5 rounded-full bg-brand-coralLight text-brand-coral" data-testid="provider-plan-badge">
            Plano: {PLAN_LABEL[provider.plan] || provider.plan}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="provider-stats">
        {[
          { icon: Users, label: "Leads recebidos", value: stats?.leads ?? 0 },
          { icon: Star, label: "Avaliação média", value: stats?.rating_avg || "—" },
          { icon: BadgeCheck, label: "Avaliações", value: stats?.rating_count ?? 0 },
          { icon: Crown, label: "Plano atual", value: PLAN_LABEL[provider.plan] || provider.plan },
        ].map((s, i) => (
          <div key={i} className="bg-white rounded-2xl p-5 border">
            <s.icon className="w-5 h-5 text-brand-coral mb-2" />
            <p className="font-heading font-bold text-2xl text-brand-graphite">{s.value}</p>
            <p className="text-xs text-muted-foreground">{s.label}</p>
          </div>
        ))}
      </div>

      {provider.plan === "trial" && (
        <div className="bg-brand-mustardLight border border-brand-mustard/40 rounded-2xl p-5 mb-8 flex flex-wrap items-center justify-between gap-4" data-testid="trial-banner">
          <div>
            <p className="font-heading font-semibold text-brand-graphite">Seu trial de 7 dias está ativo</p>
            <p className="text-sm text-muted-foreground">Assine um plano para manter seu perfil visível e desbloquear destaque e estatísticas.</p>
          </div>
          <Link to="/planos" data-testid="upgrade-plan-cta"><Button className="bg-brand-coral hover:bg-brand-coralDark text-white rounded-full">Ver planos</Button></Link>
        </div>
      )}

      <div className="bg-white rounded-2xl p-6 border" data-testid="provider-edit-form">
        <h2 className="font-heading font-semibold text-xl text-brand-graphite mb-5">Editar perfil</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="md:col-span-2"><Label>Descrição</Label><Textarea value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} className="mt-1" data-testid="edit-description" /></div>
          <div><Label>Telefone</Label><Input value={edit.phone} onChange={(e) => setEdit({ ...edit, phone: e.target.value })} className="mt-1" data-testid="edit-phone" /></div>
          <div><Label>WhatsApp</Label><Input value={edit.whatsapp} onChange={(e) => setEdit({ ...edit, whatsapp: e.target.value })} className="mt-1" data-testid="edit-whatsapp" /></div>
          <div><Label>Endereço</Label><Input value={edit.address} onChange={(e) => setEdit({ ...edit, address: e.target.value })} className="mt-1" data-testid="edit-address" /></div>
          <div><Label>Horários</Label><Input value={edit.hours} onChange={(e) => setEdit({ ...edit, hours: e.target.value })} className="mt-1" data-testid="edit-hours" /></div>
        </div>

        <div className="mt-5">
          <Label>Serviços e preços</Label>
          {edit.services.map((s, i) => (
            <div key={i} className="grid grid-cols-[1fr_120px_auto] gap-2 mt-2">
              <Input value={s.name} placeholder="Serviço" onChange={(e) => { const ss = [...edit.services]; ss[i] = { ...s, name: e.target.value }; setEdit({ ...edit, services: ss }); }} data-testid={`service-name-${i}`} />
              <Input type="number" value={s.price} placeholder="R$" onChange={(e) => { const ss = [...edit.services]; ss[i] = { ...s, price: Number(e.target.value) }; setEdit({ ...edit, services: ss }); }} data-testid={`service-price-${i}`} />
              <Button variant="ghost" size="icon" onClick={() => setEdit({ ...edit, services: edit.services.filter((_, j) => j !== i) })}><Trash2 className="w-4 h-4 text-destructive" /></Button>
            </div>
          ))}
          <Button variant="outline" size="sm" className="mt-3" onClick={() => setEdit({ ...edit, services: [...edit.services, { name: "", price: 0 }] })} data-testid="add-service-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Adicionar serviço
          </Button>
        </div>

        <div className="mt-5">
          <Label>Fotos do negócio</Label>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-2" data-testid="provider-photos-grid">
            {(edit.photos || []).map((ph, i) => (
              <div key={i} className="relative group">
                <img src={photoUrl(ph)} alt={`Foto ${i + 1}`} className="w-full h-24 object-cover rounded-xl border" data-testid={`provider-photo-${i}`} />
                <button type="button" onClick={() => setEdit({ ...edit, photos: edit.photos.filter((_, j) => j !== i) })}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`remove-photo-${i}`} title="Remover">
                  <X className="w-3.5 h-3.5" />
                </button>
                {i === 0 && <span className="absolute bottom-1 left-1 bg-brand-coral text-white text-[10px] font-semibold px-2 py-0.5 rounded-full">Capa</span>}
              </div>
            ))}
            <label className="cursor-pointer" data-testid="provider-photo-upload-label">
              <input type="file" accept="image/*" multiple className="hidden" onChange={uploadPhoto} data-testid="provider-photo-file" />
              <div className="h-24 border-2 border-dashed border-brand-coral/40 rounded-xl flex flex-col items-center justify-center text-brand-coral hover:bg-brand-coralLight/50 transition-colors text-xs gap-1">
                <ImagePlus className="w-5 h-5" />
                {uploading ? "Enviando..." : "Adicionar"}
              </div>
            </label>
          </div>
          <p className="text-xs text-muted-foreground mt-1.5">A primeira foto vira a capa do seu perfil. No celular, toque para tirar foto ou escolher da galeria.</p>
        </div>

        <Button onClick={save} disabled={saving} className="bg-brand-coral hover:bg-brand-coralDark text-white rounded-full mt-6" data-testid="save-profile-btn">
          {saving ? "Salvando..." : "Salvar alterações"}
        </Button>
      </div>
    </div>
  );
}
